import type { KaiSlashCommand } from "@/services/kaiApi";

/**
 * Minimal offline fallback catalogue, used only when the `/slash-commands`
 * fetch fails (dev / offline). Mirrors the server ids for the always-available
 * core commands; route-gated, write, and role-only commands are intentionally
 * omitted since we cannot RBAC-filter them client-side.
 */
export const FALLBACK_SLASH_COMMANDS: KaiSlashCommand[] = [
  {
    id: "ops-cutoffs",
    label: "Cutoffs",
    hint: "What's approaching cutoff soon",
    category: "Operations",
    placeholder: "optional: lane, carrier, or time window",
  },
  {
    id: "ops-departures",
    label: "Departures",
    hint: "Bookings departing soon",
    category: "Operations",
    placeholder: "optional: time window or lane",
  },
  {
    id: "sailings",
    label: "Sailings",
    hint: "Carrier sailing schedules on a lane",
    category: "Operations",
    placeholder: "e.g. Ningbo → Ashdod, 40HC, MSC, next 6 weeks",
  },
  {
    id: "ops-status",
    label: "Shipment status",
    hint: "Summarize active shipments by status",
    category: "Operations",
    placeholder: "optional: filter (carrier, lane, customer)",
  },
  {
    id: "rates",
    label: "Find rates",
    hint: "Search and compare ocean rates",
    category: "Rates",
    placeholder: "e.g. Ningbo → Ashdod, 40HC, MSC",
  },
  {
    id: "rates-catchup",
    label: "Rate changes",
    hint: "Recent rate & surcharge changes",
    category: "Rates",
    placeholder: "optional: time window or lane",
  },
  {
    id: "quote",
    label: "Heavy cargo quote",
    hint: "Quote overweight cargo with OWS",
    category: "Rates",
    placeholder: "e.g. 28t Ningbo → Ashdod, MSC",
  },
  {
    id: "feed",
    label: "Activity feed",
    hint: "Show the operational feed",
    category: "Activity",
    placeholder: "e.g. rebooks, customer Italplast, my feed",
  },
  {
    id: "feed-event",
    label: "Review event",
    hint: "Explain & act on one event",
    category: "Activity",
    placeholder: "describe the event, or use Ask Kai on a row",
  },
  {
    id: "emails-review",
    label: "Review emails",
    hint: "Summarize & drill into mail",
    category: "Activity",
    placeholder: "e.g. from MSC yesterday, order 160158, consignee Italplast",
  },
  {
    id: "show-documents",
    label: "Show documents",
    hint: "Doc-intel files to review on a job or filter",
    category: "Operations",
    placeholder: "e.g. order 161683, consignee Italplast, sales Maria",
  },
  {
    id: "support",
    label: "Contact support",
    hint: "Open a support case with Twido",
    category: "Help",
    placeholder: "describe your issue or request",
  },
  {
    id: "help",
    label: "How to use Twido",
    hint: "Guidance on using the product",
    category: "Help",
    placeholder: "what do you want to do?",
  },
];

/** Tailwind classes for an inline slash-command pill (distinct from `@` pills). */
export function slashPillClassName(): string {
  return "bg-indigo-500/15 text-indigo-700 dark:text-indigo-300";
}

/** Find a command in a catalogue by id. */
export function findSlashCommand(
  commands: KaiSlashCommand[],
  id: string,
): KaiSlashCommand | undefined {
  return commands.find((c) => c.id === id);
}

/**
 * Infer the leading `/` command from persisted message text when metadata is
 * missing (messages sent before slash persistence). Uses longest label match.
 */
export function inferSlashCommandFromContent(
  text: string,
  commands: KaiSlashCommand[],
): { id: string; label: string } | undefined {
  const trimmed = text.trimStart();
  if (!trimmed.startsWith("/")) return undefined;
  let best: KaiSlashCommand | undefined;
  for (const cmd of commands) {
    const marker = `/${cmd.label}`;
    if (
      trimmed.startsWith(marker) &&
      (!best || marker.length > `/${best.label}`.length)
    ) {
      best = cmd;
    }
  }
  return best ? { id: best.id, label: best.label } : undefined;
}

/** A category group for the dropdown, preserving catalogue order. */
export interface SlashCommandGroup {
  category: string;
  commands: KaiSlashCommand[];
}

/**
 * Group commands by category in first-seen order, so the dropdown renders
 * stable, labelled sections without re-sorting the server's ordering.
 */
export function groupSlashCommands(
  commands: KaiSlashCommand[],
): SlashCommandGroup[] {
  const groups: SlashCommandGroup[] = [];
  const byCategory = new Map<string, SlashCommandGroup>();
  for (const command of commands) {
    let group = byCategory.get(command.category);
    if (!group) {
      group = { category: command.category, commands: [] };
      byCategory.set(command.category, group);
      groups.push(group);
    }
    group.commands.push(command);
  }
  return groups;
}
